"use client";

import { jsPDF } from "jspdf";
import { type Styles } from "jspdf-autotable";

// date
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";

//Parts
import footer from "./parts/footer";
import header from "./parts/header";
import table from "./parts/table";

// Fonts
import "../../../fonts/Inter-bold.js";
import "../../../fonts/Inter-extrabold.js";
import "../../../fonts/Inter-light.js";
import "../../../fonts/Inter-normal.js";

const downloadPDF = async (config: {
  saleTitle: string | null;
  saleDate: Timestamp | null;
  reportTitle: string;
  marketDetails: {
    logo?: string;
    name?: string;
    email?: string;
    phone?: string;
    headerFillColor?: string;
  };
  data: { [key: string]: string | number | null }[];
  headers?: string[] | { dataKey: string; header: string }[];
  columnStyles?: { [key: string]: Partial<Styles> };
  bodyStyles?: Partial<Styles>;
  headStyles?: Partial<Styles>;
  filename?: string;
  isClientSide?: boolean;
}) => {
  // Extract Config
  let {
    data,
    headers,
    saleDate,
    saleTitle,
    reportTitle,
    filename,
    marketDetails,
    columnStyles,
    bodyStyles,
    headStyles,
  } = config;

  // Header
  let pdfTitle = `${saleTitle} | ${reportTitle}`;
  let subTitle = "";

  const issuedAt = saleDate as Timestamp;
  const date = issuedAt.toDate();

  // If this is a valid date
  if (date) {
    // Format the date
    subTitle = format(date, "E, d MMM yyyy").toString();
  }

  // Document Setup & Configuration
  const doc = new jsPDF({
    putOnlyUsedFonts: true,
    compress: true,
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // Table Data
  const tableData = convertArrayOfObjectsToTable(data, headers);

  // Header & Footer Calculations
  header(doc, {
    output: false,
    title: pdfTitle,
    subTitle: subTitle,
    textFillColor: marketDetails.headerFillColor ?? "#294F43",
  });
  await footer(doc, {
    output: false,
    textFillColor: "#294F43",
    marketDetails,
  });

  // Main Body
  table(doc, {
    ...tableData,
    bodyStyles: bodyStyles,
    columnStyles: columnStyles,
    headStyles: headStyles,
    headerFillColor: marketDetails.headerFillColor ?? "#294F43",
    headerTextColor: "#FFFFFF",
    stripeFillColor: "#EFEFEF",
    stripeTextColor: "#616161",
  });

  // Header Footer
  header(doc, {
    title: pdfTitle,
    subTitle: subTitle,
    output: true,
    textFillColor: marketDetails.headerFillColor ?? "#294F43",
  });

  await footer(doc, {
    textFillColor: marketDetails.headerFillColor ?? "#294F43",
    marketDetails,
    output: true,
  });

  doc.save(filename);
};

export default downloadPDF;

// Helper
function convertArrayOfObjectsToTable(
  array: { [key: string]: string | number | null }[],
  headers?: string[] | { dataKey: string; header: string }[]
) {
  // Body is the data
  let body = array;

  let headersToUse = headers || Object.keys(array[0]);

  // Columns are the keys
  let columns = headersToUse.map((header) => {
    // If the header is an object, then we are already in the correct format
    if (typeof header === "object") {
      return header;
    }

    let humanReadableHeader = header;

    // Convert from camelCase to human readable
    humanReadableHeader = humanReadableHeader.replace(/([A-Z])/g, " $1");

    // Uppercase the first character
    humanReadableHeader =
      humanReadableHeader.charAt(0).toUpperCase() +
      humanReadableHeader.slice(1);

    // Hook for custom headers
    switch (header) {
      case "lotNumber": {
        humanReadableHeader = "Lot No.";
        break;
      }
      case "quantityInLot": {
        humanReadableHeader = "Qty";
        break;
      }
      case "saleStatus": {
        humanReadableHeader = "Status";
        break;
      }
      case "averageWeightKg": {
        humanReadableHeader = "Averge Weight (kg)";
        break;
      }
      case "pricePerHeadInCents": {
        humanReadableHeader = "Price Per Head (cents)";
        break;
      }
    }

    return { header: humanReadableHeader, dataKey: header };
  });

  return {
    body,
    columns,
  };
}
